import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getConsolidationErrorMessage,
    useGetConsolidationBasisOptionsMutation,
} from "@api/consolidation.api";
import {
    ConsolidationBasisOptionsResult,
    ConsolidationEligibilityBody,
    ConsolidationRelationshipResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationRole } from "./ConsolidationSelectEntitiesStep";
import {
    getConsolidationCreationBasisEntityUuids,
    getDefaultConsolidationLedgerSelections,
    isLatestBasisOptionsRequest,
} from "./consolidationBasis.helpers";

export enum ConsolidationStep {
    SelectEntities = "selectEntities",
    ConfirmGroup = "confirmGroup",
}

interface ConsolidationSetupState {
    step: ConsolidationStep;
    selectedEntityUuids: string[];
    parentEntityUuid: string | null;
    subsidiaryEntityUuids: string[];
    ledgerOptionsByEntityUuid: ConsolidationBasisOptionsResult["ledgerOptionsByEntityUuid"];
    ledgerIdsByEntityUuid: Record<string, string>;
    isLoadingBasisOptions: boolean;
    basisOptionsError: string | null;
    isDirectConfirmation: boolean;
    toggleEntity: (uuid: string) => void;
    toggleAll: (filteredUuids: string[]) => void;
    setRole: (uuid: string, role: ConsolidationRole) => void;
    setSelectedLedger: (entityUuid: string, ledgerId: string) => void;
    loadBasisOptions: () => void;
    goBack: () => void;
    resetSetup: () => void;
}

function getInitialSelectedEntityUuids(
    initialSelection: ConsolidationEligibilityBody | undefined,
): string[] {
    if (!initialSelection) {
        return [];
    }
    return [
        initialSelection.parentEntityUuid,
        ...initialSelection.memberEntityUuids,
    ];
}

export function useConsolidationSetupState(
    workspaceId: string,
    relationships: ConsolidationRelationshipResponse[],
    initialSelection?: ConsolidationEligibilityBody,
): ConsolidationSetupState {
    const isDirectConfirmation = initialSelection !== undefined;
    const { mutate: getBasisOptions } =
        useGetConsolidationBasisOptionsMutation(workspaceId);
    const [step, setStep] = useState(() =>
        isDirectConfirmation
            ? ConsolidationStep.ConfirmGroup
            : ConsolidationStep.SelectEntities,
    );
    const [selectedEntityUuids, setSelectedEntityUuids] = useState<string[]>(
        () => getInitialSelectedEntityUuids(initialSelection),
    );
    const [parentEntityUuid, setParentEntityUuid] = useState<string | null>(
        () => initialSelection?.parentEntityUuid ?? null,
    );
    const [ledgerOptionsByEntityUuid, setLedgerOptionsByEntityUuid] = useState<
        ConsolidationBasisOptionsResult["ledgerOptionsByEntityUuid"]
    >({});
    const [ledgerIdsByEntityUuid, setLedgerIdsByEntityUuid] = useState<
        Record<string, string>
    >({});
    const [isLoadingBasisOptions, setIsLoadingBasisOptions] = useState(false);
    const [basisOptionsError, setBasisOptionsError] = useState<string | null>(
        null,
    );
    const basisOptionsRequestId = useRef(0);
    const didAutoLoadBasisOptions = useRef(false);

    const subsidiaryEntityUuids = useMemo(
        () => selectedEntityUuids.filter((uuid) => uuid !== parentEntityUuid),
        [selectedEntityUuids, parentEntityUuid],
    );

    const clearBasisOptions = useCallback(() => {
        basisOptionsRequestId.current += 1;
        setLedgerOptionsByEntityUuid({});
        setLedgerIdsByEntityUuid({});
        setIsLoadingBasisOptions(false);
        setBasisOptionsError(null);
    }, []);

    const resetToInitialSelection = useCallback(() => {
        clearBasisOptions();
        didAutoLoadBasisOptions.current = false;
        if (initialSelection) {
            setStep(ConsolidationStep.ConfirmGroup);
            setSelectedEntityUuids(
                getInitialSelectedEntityUuids(initialSelection),
            );
            setParentEntityUuid(initialSelection.parentEntityUuid);
            return;
        }
        setStep(ConsolidationStep.SelectEntities);
        setSelectedEntityUuids([]);
        setParentEntityUuid(null);
    }, [clearBasisOptions, initialSelection]);

    useEffect(() => {
        resetToInitialSelection();
    }, [resetToInitialSelection, workspaceId]);

    const setSelectedLedger = useCallback(
        (entityUuid: string, ledgerId: string) => {
            setLedgerIdsByEntityUuid((previous) => ({
                ...previous,
                [entityUuid]: ledgerId,
            }));
        },
        [],
    );

    const toggleEntity = useCallback(
        (uuid: string) => {
            clearBasisOptions();
            setSelectedEntityUuids((previous) => {
                if (previous.includes(uuid)) {
                    if (parentEntityUuid === uuid) {
                        setParentEntityUuid(null);
                    }
                    return previous.filter((value) => value !== uuid);
                }
                if (parentEntityUuid === null) {
                    setParentEntityUuid(uuid);
                }
                return [...previous, uuid];
            });
        },
        [clearBasisOptions, parentEntityUuid],
    );

    const toggleAll = useCallback(
        (filteredUuids: string[]) => {
            clearBasisOptions();
            const selectedSet = new Set(selectedEntityUuids);
            const areAllSelected =
                filteredUuids.length > 0 &&
                filteredUuids.every((uuid) => selectedSet.has(uuid));
            if (areAllSelected) {
                const filteredSet = new Set(filteredUuids);
                const remainingEntityUuids = selectedEntityUuids.filter(
                    (uuid) => !filteredSet.has(uuid),
                );
                setSelectedEntityUuids(remainingEntityUuids);
                if (parentEntityUuid && filteredSet.has(parentEntityUuid)) {
                    setParentEntityUuid(remainingEntityUuids[0] ?? null);
                }
                return;
            }
            const mergedEntityUuids = [...selectedEntityUuids];
            for (const uuid of filteredUuids) {
                if (!selectedSet.has(uuid)) {
                    mergedEntityUuids.push(uuid);
                }
            }
            setSelectedEntityUuids(mergedEntityUuids);
            if (parentEntityUuid === null) {
                setParentEntityUuid(mergedEntityUuids[0] ?? null);
            }
        },
        [clearBasisOptions, selectedEntityUuids, parentEntityUuid],
    );

    const setRole = useCallback(
        (uuid: string, role: ConsolidationRole) => {
            clearBasisOptions();
            setSelectedEntityUuids((previous) =>
                previous.includes(uuid) ? previous : [...previous, uuid],
            );
            if (role === "parent") {
                setParentEntityUuid(uuid);
            } else if (parentEntityUuid === uuid) {
                setParentEntityUuid(null);
            }
        },
        [clearBasisOptions, parentEntityUuid],
    );

    const loadBasisOptions = useCallback(() => {
        if (!parentEntityUuid || subsidiaryEntityUuids.length === 0) {
            return;
        }

        const requestId = basisOptionsRequestId.current + 1;
        basisOptionsRequestId.current = requestId;
        setLedgerOptionsByEntityUuid({});
        setLedgerIdsByEntityUuid({});
        setBasisOptionsError(null);
        setIsLoadingBasisOptions(true);
        setStep(ConsolidationStep.ConfirmGroup);

        const entityUuids = getConsolidationCreationBasisEntityUuids({
            parentEntityUuid,
            memberEntityUuids: subsidiaryEntityUuids,
            relationships,
        });
        getBasisOptions(
            {
                entityUuids,
            },
            {
                onSuccess: (result) => {
                    if (
                        !isLatestBasisOptionsRequest(
                            requestId,
                            basisOptionsRequestId.current,
                        )
                    ) {
                        return;
                    }
                    setLedgerOptionsByEntityUuid(
                        result.ledgerOptionsByEntityUuid,
                    );
                    setLedgerIdsByEntityUuid(
                        getDefaultConsolidationLedgerSelections(
                            result.ledgerOptionsByEntityUuid,
                        ),
                    );
                    setIsLoadingBasisOptions(false);
                },
                onError: (requestError) => {
                    if (
                        !isLatestBasisOptionsRequest(
                            requestId,
                            basisOptionsRequestId.current,
                        )
                    ) {
                        return;
                    }
                    setBasisOptionsError(
                        getConsolidationErrorMessage(
                            requestError,
                            "Failed to load consolidation basis options",
                        ),
                    );
                    setIsLoadingBasisOptions(false);
                },
            },
        );
    }, [
        getBasisOptions,
        parentEntityUuid,
        relationships,
        subsidiaryEntityUuids,
    ]);

    useEffect(() => {
        if (!isDirectConfirmation || didAutoLoadBasisOptions.current) {
            return;
        }
        if (!parentEntityUuid || subsidiaryEntityUuids.length === 0) {
            return;
        }
        didAutoLoadBasisOptions.current = true;
        loadBasisOptions();
    }, [
        isDirectConfirmation,
        loadBasisOptions,
        parentEntityUuid,
        subsidiaryEntityUuids,
    ]);

    const goBack = useCallback(() => {
        clearBasisOptions();
        setStep(ConsolidationStep.SelectEntities);
    }, [clearBasisOptions]);

    const resetSetup = useCallback(() => {
        resetToInitialSelection();
    }, [resetToInitialSelection]);

    return {
        step,
        selectedEntityUuids,
        parentEntityUuid,
        subsidiaryEntityUuids,
        ledgerOptionsByEntityUuid,
        ledgerIdsByEntityUuid,
        isLoadingBasisOptions,
        basisOptionsError,
        isDirectConfirmation,
        toggleEntity,
        toggleAll,
        setRole,
        setSelectedLedger,
        loadBasisOptions,
        goBack,
        resetSetup,
    };
}
